import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAdminLogin, useGraphQLError } from '../hooks/useGraphQL';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Alert, 
  Typography, 
  Space, 
  Divider,
  Row,
  Col
} from 'antd';
import { 
  EyeTwoTone, 
  EyeInvisibleOutlined, 
  LockOutlined, 
  MailOutlined, 
  LoginOutlined 
} from '@ant-design/icons';
import { useTranslation } from '../hooks/useTranslation';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const [adminLogin] = useAdminLogin();
  const { handleError } = useGraphQLError();
  const { t } = useTranslation();

  const handleSubmit = async (values: { email: string; password: string }) => {
    setLoading(true);
    setError('');

    try {
      const response = await adminLogin({
        variables: {
          loginDto: {
            email: values.email,
            password: values.password
          }
        }
      });

      if (response.data?.adminLogin) {
        const { adminUser, accessToken, refreshToken } = response.data.adminLogin;
        
        // Calculate token expiration (1 hour from now)
        const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
        
        // Ensure all required fields are present and permissions are properly loaded
        const userWithPermissions = {
          ...adminUser,
          permissions: adminUser.permissions || [],
          is_super_admin: adminUser.is_super_admin || false,
          role: adminUser.role || 'ADMIN'
        };
        
        console.log('Login response - adminUser:', adminUser);
        console.log('Login response - permissions:', adminUser.permissions);
        console.log('Login response - userWithPermissions:', userWithPermissions);
        
        login({
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_at: expiresAt
        }, userWithPermissions);
      } else {
        setError(t('auth.loginError'));
      }
    } catch (err: any) {
      const errorMessage = handleError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Row justify="center" align="middle" style={{ width: '100%', maxWidth: 1200 }}>
        <Col xs={24} sm={20} md={16} lg={12} xl={10}>
          <Card 
            style={{ 
              borderRadius: 16,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{
                width: 64,
                height: 64,
                backgroundColor: '#1890ff',
                borderRadius: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                background: 'linear-gradient(135deg, #1890ff, #722ed1)'
              }}>
                <LoginOutlined style={{ fontSize: 28, color: 'white' }} />
              </div>
              <Title level={2} style={{ margin: 0, color: '#1f2937' }}>
                {t('auth.loginTitle')}
              </Title>
              <Text type="secondary">
                Sign in to your account to continue
              </Text>
            </div>

            {error && (
              <Alert
                message={t('auth.login')}
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: 24 }}
                closable
                onClose={() => setError('')}
              />
            )}

            <Form
              name="login"
              onFinish={handleSubmit}
              layout="vertical"
              size="large"
              initialValues={{
                email: '',
                password: ''
              }}
            >
              <Form.Item
                label={t('auth.username')}
                name="email"
                rules={[
                  { required: true, message: 'Please input your email!' },
                  { type: 'email', message: 'Please enter a valid email!' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="Enter your email"
                />
              </Form.Item>

              <Form.Item
                label={t('auth.password')}
                name="password"
                rules={[{ required: true, message: 'Please input your password!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter your password"
                  iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                  style={{ height: 48 }}
                >
                  {loading ? t('common.loading') : t('auth.loginButton')}
                </Button>
              </Form.Item>
            </Form>

            <Divider>
              <Text type="secondary">Demo Credentials</Text>
            </Divider>
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                © 2024 Call Center. All rights reserved.
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Login; 